// src/pages/Profile.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import DashboardLayout from '../layouts/DashboardLayout';
import Form from '../components/shared/Form';
import Input from '../components/shared/Input';
import Button from '../components/shared/Button';
import Select from '../components/shared/Select';  // Use updated Select component
import { getUserProfile, updateUserProfile } from '../modules/auth/authService';
import { useAuth } from '../modules/auth/AuthContext';  // To get current user
import { FaUser, FaEnvelope, FaPhone, FaUserTag } from 'react-icons/fa';  // Import icons
import LoadingSpinner from '../components/shared/LoadingSpinner';
import { getDirectCallKey, rotateDirectCallKey } from '../services/communicationService';

const InputWithIcon = styled.div`
  position: relative;
  margin-bottom: ${(props) => props.theme.spacing(3)};

  svg {
    position: absolute;
    left: 16px;
    top: 50%;
    transform: translateY(-100%);
    font-size: 1.2rem;
    color: ${(props) => props.theme.colors.primary};
  }

  input, select {
    padding-left: 45px;  // Leave space for the icon
  }
`;

const KeySection = styled.section`
  margin-top: ${(props) => props.theme.spacing(5)};
  padding: ${(props) => props.theme.spacing(3)};
  background: ${(props) => props.theme.colors.surface};
  border-radius: ${(props) => props.theme.borderRadius};
  border: 1px solid ${(props) => props.theme.colors.inputGray};
`;

const KeyRow = styled.div`
  display: flex;
  gap: ${(props) => props.theme.spacing(2)};
  margin-top: ${(props) => props.theme.spacing(2)};
  flex-wrap: wrap;
`;

const KeyBox = styled.code`
  display: block;
  padding: ${(props) => props.theme.spacing(2)};
  background: ${(props) => props.theme.colors.background};
  border-radius: ${(props) => props.theme.borderRadius};
  font-family: monospace;
  word-break: break-all;
  margin-top: ${(props) => props.theme.spacing(2)};
  color: ${(props) => props.theme.colors.textPrimary};
`;

const Warning = styled.p`
  margin-top: ${(props) => props.theme.spacing(2)};
  color: ${(props) => props.theme.colors.textSecondary};
  font-size: 0.85rem;
  line-height: 1.4;
`;

// Simple phone input that doesn't use react-text-mask
const SimplePhoneInput = ({ value, onChange }) => {
  // Format phone number as user types
  const handlePhoneChange = (e) => {
    let input = e.target.value.replace(/\D/g, ''); // Remove non-digits
    let formatted = '';
    
    if (input.length > 0) {
      // Format as (XXX) XXX-XXXX
      if (input.length <= 3) {
        formatted = `(${input}`;
      } else if (input.length <= 6) {
        formatted = `(${input.slice(0, 3)}) ${input.slice(3)}`;
      } else {
        formatted = `(${input.slice(0, 3)}) ${input.slice(3, 6)}-${input.slice(6, 10)}`;
      }
    }
    
    // Call the original onChange with our formatted value
    onChange({
      target: {
        value: formatted
      }
    });
  };

  return (
    <Input
      icon={<FaPhone />}
      type="tel"
      placeholder="Phone Number"
      value={value || ''}
      onChange={handlePhoneChange}
      maxLength={14} // (XXX) XXX-XXXX = 14 characters
    />
  );
};

const Profile = () => {
  const { user } = useAuth();  // Get the current authenticated user
  const [profile, setProfile] = useState({
    username: '',
    email: '',
    phoneNumber: '',
    role: 'user',  // Default to user
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [directKey, setDirectKey] = useState(null);
  const [directKeyError, setDirectKeyError] = useState(null);
  const [directKeyBusy, setDirectKeyBusy] = useState(false);

  console.log("Profile component loaded");

  // Fetch the profile data when the component loads
  useEffect(() => {
    if (!user || !user.id) {
      setLoading(false);
      setError('User information not available');
      console.log('User information not available');
      return;
    }

    async function fetchProfile() {
      try {
        console.log('Fetching profile for user ID:', user.id);
        const data = await getUserProfile(user.id);  // Pass user ID to fetch profile
        console.log('Received profile data:', data);
        
        setProfile({
          username: data.username || '',
          email: data.email || '',
          phoneNumber: data.phoneNumber || '',
          role: data.role || 'user'
        });
        setLoading(false);
      } catch (err) {
        console.error('Error fetching profile:', err);
        setError('Failed to load profile');
        setLoading(false);
      }
    }
    
    fetchProfile();
  }, [user]);

  const handleShowKey = async () => {
    setDirectKeyBusy(true);
    setDirectKeyError(null);
    try {
      const { key } = await getDirectCallKey();
      setDirectKey(key);
    } catch (err) {
      setDirectKeyError(err.response?.data?.message || err.message || 'Failed to load key');
    } finally {
      setDirectKeyBusy(false);
    }
  };

  const handleRotateKey = async () => {
    // eslint-disable-next-line no-alert
    const confirmed = window.confirm(
      'Rotating your direct-call key will break EVERY direct-call URL you have saved. ' +
      'You will need to rebuild any saved links. Continue?'
    );
    if (!confirmed) return;

    setDirectKeyBusy(true);
    setDirectKeyError(null);
    try {
      const { key } = await rotateDirectCallKey();
      setDirectKey(key);
    } catch (err) {
      setDirectKeyError(err.response?.data?.message || err.message || 'Failed to rotate key');
    } finally {
      setDirectKeyBusy(false);
    }
  };

  const handleCopyKey = () => {
    if (!directKey) return;
    navigator.clipboard?.writeText(directKey);
  };

  const exampleUrl = () => {
    if (!directKey) return '';
    const fromDigits = (profile.phoneNumber || '').replace(/\D/g, '');
    const fromParam = fromDigits ? `+1${fromDigits.slice(-10)}` : '<your+phone>';
    return `https://crm.mrpatchasphalt.com/direct-call?to=<lead+phone>&from=${fromParam}&leadId=<lead+id>&k=${directKey}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!user || !user.id) {
        setError('User information not available');
        return;
      }
      
      await updateUserProfile(user.id, profile);  // Pass user ID and updated profile data
      setSuccess('Profile updated successfully');
      setError(null);
    } catch (err) {
      console.error("Failed to update profile:", err);
      setError('Failed to update profile: ' + (err.message || 'Unknown error'));
      setSuccess(null);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <LoadingSpinner />
          <p>Loading profile data...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <h2>Update Profile</h2>
      {success && <p style={{ color: 'green' }}>{success}</p>}
      {error && <p style={{ color: 'red' }}>{error}</p>}
      
      <Form onSubmit={handleSubmit}>
        {/* Username with Icon */}
        <InputWithIcon>
          <FaUser />
          <Input
            type="text"
            placeholder="Username"
            value={profile.username || ''}
            onChange={(e) => setProfile({ ...profile, username: e.target.value })}
          />
        </InputWithIcon>

        {/* Email with Icon */}
        <InputWithIcon>
          <FaEnvelope />
          <Input
            type="email"
            placeholder="Email"
            value={profile.email || ''}
            onChange={(e) => setProfile({ ...profile, email: e.target.value })}
          />
        </InputWithIcon>

        {/* Simple Phone Input with Icon - avoiding the reactTextMask issues */}
        <InputWithIcon>
          <SimplePhoneInput
            value={profile.phoneNumber || ''}
            onChange={(e) => setProfile({ ...profile, phoneNumber: e.target.value })}
          />
        </InputWithIcon>

        {/* Role (only shown if the user is an admin) with Icon */}
        {user && user.role === 'admin' && (
          <InputWithIcon>
            <FaUserTag />
            <Select
              value={profile.role || 'user'}
              onChange={(e) => setProfile({ ...profile, role: e.target.value })}
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </Select>
          </InputWithIcon>
        )}

        <Button type="submit">Update Profile</Button>
      </Form>

      <KeySection>
        <h3 style={{ margin: 0 }}>Direct-call link</h3>
        <Warning>
          Your direct-call key lets you place a click-to-call from a pre-saved URL
          without logging in. Treat this key like a password — anyone who has it
          can place calls attributed to your account. Rotate it if it leaks.
        </Warning>

        {directKeyError && <p style={{ color: 'red' }}>{directKeyError}</p>}

        <KeyRow>
          <Button type="button" onClick={handleShowKey} disabled={directKeyBusy}>
            {directKey ? 'Refresh key' : 'Show my key'}
          </Button>
          <Button type="button" onClick={handleRotateKey} disabled={directKeyBusy}>
            Rotate key
          </Button>
          {directKey && (
            <Button type="button" onClick={handleCopyKey} disabled={directKeyBusy}>
              Copy key
            </Button>
          )}
        </KeyRow>

        {directKey && (
          <>
            <KeyBox>{directKey}</KeyBox>
            <Warning>Example URL (replace placeholders per lead):</Warning>
            <KeyBox>{exampleUrl()}</KeyBox>
          </>
        )}
      </KeySection>
    </DashboardLayout>
  );
};

export default Profile;